import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '../../app/AppStore';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { IUserSettings } from '../models/IUserSettings';
import { Module } from '../Module';
import { LanguagesInstance } from '../resources/Languages';
import { LocalesInstance } from '../resources/Locales';
import { ILanguage } from '../ServerApi/types/ILanguage';
import { ILocale } from '../ServerApi/types/ILocale';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { UserSettingsNotificationParams, UserSettingsNotificationType, UserSettingsNotification } from '../../userSettings/notifications/UserSettingsNotification';
import { UserSettings } from '../../userSettings/services/resources/UserSettings';
import { values } from 'underscore';

export interface IEditLanguageAndLocaleCtrls {
  save(): void;
  canSave(): void;
  isBusy(): void;
}

export interface IEditLanguageAndLocaleScope extends ng.IScope {
  controls: IEditLanguageAndLocaleCtrls;
  serverUser: IServerUserInfo;
  userSettings: IUserSettings;
  onSaveComplete(): any;

  language: string;
  locale: string;

  languages: ILanguage[];
  locales: ILocale[];

  model: { language: string, locale: string };
}

Module.instance().directive('tbEditLanguageAndLocale', <any>[
  '$q',
  (
    $q: ng.IQService) => {
    return {
      restrict: 'E',
      replace: 'true',
      templateUrl: 'editLanguageAndLocale.html',
      scope: {
        controls: '=?',
        serverUser: '=',
        language: '=?',
        locale: '=?'
      },
      link: (scope: IEditLanguageAndLocaleScope) => {
        const serverService = getSessionManagementServiceInstance();
        // these are shown as 'Unspecified' in the UI, and tell the server to use the server language/locale for the user.
        const unspecifiedLanguage = LanguagesInstance.unspecifiedLanguage();
        const unspecifiedLocale = LocalesInstance.unspecifiedLocale();

        // the server returns no language/locale for users that haven't explicitly set one.
        // we need to map that to the special client-only value 'Unspecified' in the UI.
        const applyUnspecifiedLanguageIfNecessary = () => {
          if (!scope.language) {
            scope.language = unspecifiedLanguage.language;
          }
        };
        const applyUnspecifiedLocaleIfNecessary = () => {
          if (!scope.locale) {
            scope.locale = unspecifiedLocale.locale;
          }
        };

        applyUnspecifiedLanguageIfNecessary();
        applyUnspecifiedLocaleIfNecessary();
        scope.$watch('language', () => {
          applyUnspecifiedLanguageIfNecessary();
        });
        scope.$watch('locale', () => {
          applyUnspecifiedLocaleIfNecessary();
        });

        scope.model = {
          language: scope.language,
          locale: scope.locale
        };

        LanguagesInstance.getAll().then((langs: ILanguage[]) => {
          // add unspecified to the list to allow the user to unset their language.
          langs.unshift(unspecifiedLanguage);
          scope.languages = langs;
        });

        LocalesInstance.getAll().then((locs: ILocale[]) => {
          // add unspecified to the list to allow the user to unset their locale
          locs.unshift(unspecifiedLocale);
          scope.locales = locs;
        });

        let isBusy = false;
        const isDirty = () => {
          return isLanguageDirty() || isLocaleDirty();
        };
        const isLanguageDirty = () => scope.model.language !== scope.language;
        const isLocaleDirty = () => scope.model.locale !== scope.locale;
        const userIsViewingTheirOwnSettings = () => {
          return serverService.sessionUsername() === scope.serverUser.username
            && serverService.sessionUserDomainName() === scope.serverUser.domainName;
        };

        const maybeShowChangeLanguageNotification = (maybeUpdatedLanguagePromise: ng.IPromise<any>) => {
          maybeUpdatedLanguagePromise.then((result) => {
            if (result) {
              const userSettingsNotificationParams: UserSettingsNotificationParams = {
                result: result,
                username: scope.serverUser.username,
                userSettingsNotificationType: UserSettingsNotificationType.ChangeLanguage
              };
              UserSettingsNotification.notify(userSettingsNotificationParams);
            }
          });
        };
        const maybeShowChangeLocaleNotification = (maybeUpdatedLocalePromise: ng.IPromise<any>) => {
          maybeUpdatedLocalePromise.then((result) => {
            if (result) {
              const userSettingsNotificationParams: UserSettingsNotificationParams = {
                result: result,
                username: scope.serverUser.username,
                userSettingsNotificationType: UserSettingsNotificationType.ChangeLocale
              };
              UserSettingsNotification.notify(userSettingsNotificationParams);
            }
          });
        };

        scope.controls = {
          save: () => {
            isBusy = true;
            const maybeUpdateLanguage = isLanguageDirty() ?
              UserSettings.updateUserLanguage(scope.serverUser.id, scope.model.language) :
              $q.when(false);
            const maybeUpdateLocale = isLocaleDirty() ?
              UserSettings.updateUserLocale(scope.serverUser.id, scope.model.locale) :
              $q.when(false);
            $q.all([maybeUpdateLanguage, maybeUpdateLocale]).then(() => {

              // save completed successfully, so update the language and locale on the scope.
              scope.language = scope.model.language;
              scope.locale = scope.model.locale;
              isBusy = false;
              if (userIsViewingTheirOwnSettings()) {
                serverService.refreshSessionInfo().then(() => {
                  getAppStore().dispatch(incrementRouterSpaceReactKey());

                  // wait to show toast so that they are rendered in the new language
                  maybeShowChangeLanguageNotification(maybeUpdateLanguage);
                  maybeShowChangeLocaleNotification(maybeUpdateLocale);
                });
              } else {
                // show toast right away since we are changing for another user
                maybeShowChangeLanguageNotification(maybeUpdateLanguage);
                maybeShowChangeLocaleNotification(maybeUpdateLocale);
              }
            });
          },
          canSave: () => isDirty() && !isBusy,
          isBusy: () => isBusy
        };
      }
    };
  }
]);
